import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/agreements';

const TYPES = [
  { key: '', label: 'Tüm Tipler' },
  { key: 'DSA', label: 'DSA' },
  { key: 'SPACEMAN', label: 'Spaceman' },
  { key: 'CONTRACT', label: 'Sözleşme' },
];

const TYPE_LABEL = Object.fromEntries(TYPES.filter(t => t.key).map(t => [t.key, t.label]));

const PERIODS = [
  { key: 'ANNUAL', label: 'Yıllık' },
  { key: 'PERIODIC', label: 'Periyodik' },
  { key: 'MONTHLY', label: 'Aylık' },
];

const PERIOD_LABEL = Object.fromEntries(PERIODS.map(p => [p.key, p.label]));

const STATUS_TABS = [
  { key: '', label: 'Hepsi' },
  { key: 'ACTIVE', label: 'Aktif' },
  { key: 'PENDING', label: 'Beklemede' },
  { key: 'EXPIRED', label: 'Süresi Dolmuş' },
];

const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  PENDING: { label: 'Beklemede', tone: 'warn' },
  EXPIRED: { label: 'Süresi Doldu', tone: 'bad' },
};

const PAGE_SIZE_DEFAULT = 20;

const EMPTY_FORM = {
  customerId: '',
  customerTitle: '',
  title: '',
  type: 'CONTRACT',
  startDate: '',
  endDate: '',
  period: 'ANNUAL',
  content: '',
  attachedPdfUrl: '',
  autoDiscountPct: '',
  status: 'ACTIVE',
  signedAt: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    expiredCount: pick(data, 'expiredCount', 'ExpiredCount') ?? 0,
    pendingCount: pick(data, 'pendingCount', 'PendingCount') ?? 0,
  };
}

function statusPillFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
    bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function typePill(type) {
  const tones = {
    DSA: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8' },
    SPACEMAN: { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.30)', color: '#5b21b6' },
    CONTRACT: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569' },
  };
  const t = tones[type] || tones.CONTRACT;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 999, fontWeight: 700, fontSize: 11,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
    }}>
      {TYPE_LABEL[type] || type || '-'}
    </span>
  );
}

function badge(label, value, tone) {
  const tones = {
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
  };
  const t = tones[tone] || tones.green;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function AgreementsPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, expiredCount: 0, pendingCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        activeCount: data.activeCount,
        expiredCount: data.expiredCount,
        pendingCount: data.pendingCount,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(EMPTY_FORM);
  };

  const openEdit = async (row) => {
    const id = pick(row, 'id', 'Id');
    setEditing({ mode: 'edit', id });
    // List response Content içermiyor (büyük olduğu için) — GET ile tam kayıt çek.
    setForm({
      customerId: pick(row, 'customerId', 'CustomerId') || '',
      customerTitle: pick(row, 'customerTitle', 'CustomerTitle') || '',
      title: pick(row, 'title', 'Title') || '',
      type: pick(row, 'type', 'Type') || 'CONTRACT',
      startDate: pick(row, 'startDate', 'StartDate') || '',
      endDate: pick(row, 'endDate', 'EndDate') || '',
      period: pick(row, 'period', 'Period') || 'ANNUAL',
      content: '',
      attachedPdfUrl: pick(row, 'attachedPdfUrl', 'AttachedPdfUrl') || '',
      autoDiscountPct: pick(row, 'autoDiscountPct', 'AutoDiscountPct') ?? '',
      status: pick(row, 'status', 'Status') || 'ACTIVE',
      signedAt: pick(row, 'signedAt', 'SignedAt') || '',
    });
    setContentLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Anlaşma çekilemedi (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setForm(curr => ({
        ...curr,
        content: pick(data, 'content', 'Content') || '',
        customerTitle: pick(data, 'customerTitle', 'CustomerTitle') || curr.customerTitle,
      }));
    } catch (err) {
      showToast('error', err.message || 'İçerik çekilemedi');
    } finally {
      setContentLoading(false);
    }
  };

  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submitForm = async () => {
    if (!form.customerId.trim()) { showToast('error', 'CustomerId zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    if (!form.startDate.trim()) { showToast('error', 'Başlangıç tarihi zorunlu.'); return; }
    if (!form.endDate.trim()) { showToast('error', 'Bitiş tarihi zorunlu.'); return; }

    setSaving(true);
    try {
      const body = {
        customerId: form.customerId.trim(),
        title: form.title.trim(),
        type: form.type,
        startDate: form.startDate.trim(),
        endDate: form.endDate.trim(),
        period: form.period,
        content: form.content ?? '',
        attachedPdfUrl: form.attachedPdfUrl.trim() || null,
        autoDiscountPct: form.autoDiscountPct === '' ? null : Number(form.autoDiscountPct),
        status: form.status,
        signedAt: form.signedAt.trim() || null,
      };
      const url = editing.mode === 'edit' ? `${API_BASE}/${editing.id}` : API_BASE;
      const method = editing.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', editing.mode === 'edit' ? 'Anlaşma güncellendi.' : 'Anlaşma oluşturuldu.');
      closeEdit();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteRow) return;
    const id = pick(deleteRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Anlaşma silindi.');
      setDeleteRow(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'title', 'Title'),
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'type', 'Type'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'Type': return pick(row, 'type', 'Type') || '';
        case 'StartDate': return pick(row, 'startDate', 'StartDate') || '';
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'title',
      headerName: 'Başlık',
      flex: 1.4,
      minWidth: 200,
      valueGetter: ({ row }) => pick(row, 'title', 'Title') || '-',
    },
    {
      field: 'customerTitle',
      headerName: 'Müşteri',
      flex: 1.2,
      minWidth: 180,
      valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '(Müşteri yok)',
    },
    {
      field: 'type',
      headerName: 'Tip',
      flex: 0.6,
      minWidth: 110,
      renderCell: ({ row }) => typePill(pick(row, 'type', 'Type')),
    },
    {
      field: 'period',
      headerName: 'Dönem',
      flex: 0.6,
      minWidth: 100,
      valueGetter: ({ row }) => PERIOD_LABEL[pick(row, 'period', 'Period')] || pick(row, 'period', 'Period') || '-',
    },
    {
      field: 'dates',
      headerName: 'Tarih Aralığı',
      flex: 0.9,
      minWidth: 170,
      sortable: false,
      valueGetter: ({ row }) => {
        const s2 = pick(row, 'startDate', 'StartDate') || '?';
        const e = pick(row, 'endDate', 'EndDate') || '?';
        return `${s2} – ${e}`;
      },
    },
    {
      field: 'autoDiscountPct',
      headerName: 'İndirim',
      flex: 0.5,
      minWidth: 80,
      valueGetter: ({ row }) => {
        const v = pick(row, 'autoDiscountPct', 'AutoDiscountPct');
        return v == null ? '-' : `%${Number(v).toFixed(2).replace(/\.00$/, '')}`;
      },
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.7,
      minWidth: 130,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.9,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={() => openEdit(row)}
          >
            Düzenle
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#dc2626' }}
            onClick={() => setDeleteRow(row)}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Anlaşmalar</h1>
          <p style={s.pageSubtitle}>
            DSA / Spaceman / yıllık sözleşme master kayıtları. Mobile GetDelta?entityType=Agreement
            ile çeker; nokta detayında gösterilir. Push yönü yok.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Aktif', counts.activeCount, 'green')}
          {badge('Beklemede', counts.pendingCount, 'orange')}
          {badge('Süresi Dolmuş', counts.expiredCount, 'red')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
          <button type="button" style={s.btnPrimary} onClick={openCreate}>
            + Yeni Anlaşma
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
          <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={s.select}
          >
            {TYPES.map(t => <option key={t.key || 'all'} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Başlık / müşteri..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openEdit}
        exportFileName="anlasmalar"
      />

      {editing && (
        <div style={s.overlay} onClick={closeEdit}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editing.mode === 'edit' ? 'Anlaşmayı Düzenle' : 'Yeni Anlaşma'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeEdit}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Müşteri ID (GUID) *</label>
                  <input
                    type="text"
                    value={form.customerId}
                    onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    style={{ ...s.input, fontFamily: 'monospace', fontSize: 12 }}
                  />
                  {form.customerTitle && (
                    <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>
                      ✓ {form.customerTitle}
                    </div>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Tip *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    style={s.select}
                  >
                    {TYPES.filter(t => t.key).map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Başlık *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  style={s.input}
                />
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Başlangıç (YYYY-MM-DD) *</label>
                  <input
                    type="text"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    placeholder="2026-01-01"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bitiş (YYYY-MM-DD) *</label>
                  <input
                    type="text"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    placeholder="2026-12-31"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Dönem *</label>
                  <select
                    value={form.period}
                    onChange={(e) => setForm({ ...form, period: e.target.value })}
                    style={s.select}
                  >
                    {PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum *</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    style={s.select}
                  >
                    {STATUS_TABS.filter(t => t.key).map(t => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Oto. İndirim %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.autoDiscountPct}
                    onChange={(e) => setForm({ ...form, autoDiscountPct: e.target.value })}
                    style={s.input}
                    placeholder="(opsiyonel)"
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>İmza Tarihi</label>
                  <input
                    type="text"
                    value={form.signedAt}
                    onChange={(e) => setForm({ ...form, signedAt: e.target.value })}
                    placeholder="2026-01-15"
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Ek PDF URL</label>
                <input
                  type="text"
                  value={form.attachedPdfUrl}
                  onChange={(e) => setForm({ ...form, attachedPdfUrl: e.target.value })}
                  placeholder="https://..."
                  style={s.input}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>
                  Sözleşme İçeriği
                  {contentLoading && <span style={{ color: '#6b7280', fontSize: 12 }}> (yükleniyor...)</span>}
                </label>
                <textarea
                  rows={8}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  style={s.textarea}
                />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeEdit} disabled={saving}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={submitForm}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : (editing.mode === 'edit' ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRow && (
        <div style={s.overlay} onClick={() => setDeleteRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Anlaşmayı Sil</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 14, color: '#1f2937' }}>
                <strong>{pick(deleteRow, 'title', 'Title')}</strong> anlaşmasını silmek istediğinize emin misiniz?
              </div>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Mobile bu anlaşmayı bir sonraki sync'te artık göstermeyecek.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default AgreementsPageLegacy;
